import Polyglot from 'node-polyglot'
import isEmpty from 'lodash/isEmpty'
import find from 'lodash/find'
import capitalize from 'lodash/capitalize'

// import manually the language files we need
// TODO: do this dynamically
import 'moment/locale/fr'
import 'moment/locale/de'
import moment from 'moment'

import { getQueryParam } from 'pmt-utils/url'
import { localeEquals } from 'pmt-utils/locale'

import { retrieveMessages } from './actions'
import { getLocaleFromStorage } from './storage'

// could be dynamically imported if we want to reduce the bundle size
import localizeFlatpickr from 'pmt-ui/Calendar/localizeFlatpickr'

// force locale to be used, can be: 'debug', fr, en, etc
export const QUERY_PARAM_LOCALE = 'l'

export const getDefaultBrowserLocale = () => {
  // Define user's language. Different browsers have the user locale defined
  // on different fields on the `navigator` object, so we make sure to account
  // for these different by checking all of them
  const language =
    (navigator.languages && navigator.languages[0]) || navigator.language || navigator.userLanguage

  return language
}

//
//
//

const DEFAULT_OPTIONS = {
  defaultLocale: 'en',
  calendar: false,
}

let _locale = null
let _texts = null

//
// - calendar -> tr flatpickr ?
//
let _options = DEFAULT_OPTIONS

// create an instance of the Polyglot class, which you will use for translation.
// Polyglot is class-based so you can maintain different sets of phrases at the same time, possibly
// in different locales. This is very useful for example when serving requests with Express,
// because each request may have a different locale, and you don’t want concurrent requests to
// clobber each other’s phrases.
let _polyglot = new Polyglot({ locale: getDefaultBrowserLocale() })

export const getPolyglot = () => _polyglot

export const setPolyglot = polyglot => {
  _polyglot = polyglot
}

//
//
//

const getLanguageWithoutRegionCode = language => {
  if (isEmpty(language)) {
    return 'en'
  }

  // Split locales with a region code
  const languageWithoutRegionCode = language.toLowerCase().split(/[_-]+/)[0]

  return languageWithoutRegionCode
}

export const getCurrentLanguageWithoutRegionCode = () => {
  return getLanguageWithoutRegionCode(_locale)
}

export const isSupportedLocale = locale =>
  find(_options.locales, supportedLocale => localeEquals(supportedLocale, locale))

export const getCurrentSupportedLanguage = () => {
  let language = getLanguageWithoutRegionCode(_locale)
  if (!isSupportedLocale(language)) {
    language = _options.defaultLocale
  }
  return language
}

export const getDefaultLocale = () => {
  let locale = getDefaultBrowserLocale()

  const queryLocale = getQueryParam(QUERY_PARAM_LOCALE)
  if (!isEmpty(queryLocale) && isSupportedLocale(queryLocale)) {
    locale = queryLocale
  } else {
    // not in query param, search in storage
    const storageLocale = getLocaleFromStorage()
    if (!isEmpty(storageLocale)) {
      locale = storageLocale
    }
  }

  return locale
}

//
//
//

export const configureI18n = (options = {}) => {
  // set options
  _options = { ...DEFAULT_OPTIONS, ...options }

  if (_options.disable) {
    return
  }

  if (!globalThis.Intl) {
    console.error('Required intl Polyfill')
  }

  let locale = getDefaultLocale()
  setLocale(locale)
}

export const getSupportedLocales = () => _options.locales

/**
 * "fr_CH" => "fr-CH"
 *
 * We saw some Restaurants with fr-FR (probably because it's the default value) and some with fr_FR (probably value provided by BOV1)
 */
export const cleanLocale = locale => {
  if (!locale) {
    return null
  }
  return locale.replace('_', '-')
}

/**
 * "fr-CH" => "Français (Suisse)"
 */
export const getLocaleLabel = value => {
  try {
    const displayNamesLanguage = new Intl.DisplayNames([getLocale()], { type: 'language' })
    const displayNamesRegion = new Intl.DisplayNames([getLocale()], { type: 'region' })

    const cleanedLocale = cleanLocale(value)
    if (!cleanedLocale) {
      return value
    }

    const parts = cleanedLocale.split('-')
    const language = parts[0]
    const region = parts[1]
    if (region) {
      return (
        capitalize(displayNamesLanguage.of(language)) +
        ' (' +
        capitalize(displayNamesRegion.of(region)) +
        ')'
      )
    } else {
      return capitalize(displayNamesLanguage.of(language))
    }
  } catch (e) {
    console.error(e)
    return value
  }
}

export const getSupportedLocalesOptions = () =>
  getSupportedLocales().map(locale => ({
    value: locale,
    label: getLocaleLabel(locale),
  }))

export const setTexts = (apiConsumerTexts, restaurantCustomTexts) => {
  _texts = {
    ...(apiConsumerTexts || {}),
    ...(restaurantCustomTexts?.texts || {}),
  }
}

export const setLocale = locale => {
  _locale = locale

  let language = getLanguageWithoutRegionCode(locale)
  if (!isSupportedLocale(language)) {
    language = _options.defaultLocale
  }

  const optionsFR = {
    longDateFormat: {
      LT: 'HH[h]mm', // to have 20h00 and not 20:00 in French
    },
  }

  moment.updateLocale('fr', optionsFR)

  const optionsDE = {
    longDateFormat: {
      LT: 'HH.mm', // to have 20.15 and not 20:15 in German
    },
  }

  moment.updateLocale('de', optionsDE)

  moment.locale(language)

  if (process.env.FEATURE_FLATPICKR) {
    localizeFlatpickr(language)
  }

  // retrieve messages
  retrieveMessages(language)
}

export const getLocale = () => _locale

export const getCountryForLocale = locale => {
  if (!locale) {
    return ''
  }
  if (locale.length === 5) {
    // handle 'fr-FR' format
    const country = locale.substring(3, 5)
    return country.toLowerCase()
  }

  // trick for locales like 'en', 'fr'
  locale = locale.substring(0, 2).toLowerCase()
  switch (locale) {
    case 'sq':
      return 'al'
    case 'ar':
      return 'eg'
    case 'be':
      return 'by'
    case 'ca':
      return 'es'
    case 'zh':
      return 'cn'
    case 'cs':
      return 'cz'
    case 'da':
      return 'dk'
    case 'en':
      return 'us'
    case 'et':
      return 'ee'
    case 'el':
      return 'gr'
    case 'iw':
      return 'il'
    case 'hi':
      return 'in'
    case 'in':
      return 'id'
    case 'ga':
      return 'ie'
    case 'ja':
      return 'jp'
    case 'ko':
      return 'kr'
    case 'ms':
      return 'my'
    case 'sl':
      return 'si'
    case 'sv':
      return 'se'
    case 'uk':
      return 'ua'
    case 'vi':
      return 'vn'
    case 'sr':
      return 'rs'

    default:
      return locale
  }
}

export const getCountryCodeForLocale = locale => {
  let countryCode = getCountryForLocale(locale)

  if (countryCode) {
    countryCode = countryCode.toUpperCase()
  }

  return countryCode
}

//
//
//

const trWithLocale = (locale, key, options = {}, useKeyIfNotFound) => {
  if (locale === 'debug') {
    return key
  }

  if (isEmpty(key)) {
    console.error(`[i18n] key is empty.`)
    return null
  }

  try {
    if (_texts && !isEmpty(_texts[key])) {
      // eg. "Welcome, %{name}!" with options = { name: "John" }
      // or "%{smart_count} restaurant disponible autour de %{address} |||| %{smart_count} restaurants disponibles autour de %{address}" with options = { smart_count: 2, address: "Paris" }
      let text = _texts[key]

      // handle plural forms like polyglot does
      // (simple implementation - only 2 forms: singular and plural. separated by ||||. Languages with more complex plural rules are not supported here)
      if (options.smart_count) {
        const smartCount = parseInt(options.smart_count, 10)
        // Split the label into singular and plural forms
        const [singular, plural] = text.split('||||').map(str => str.trim())

        // Choose the correct form based on smartCount
        text = smartCount === 1 ? singular : plural
      }

      // replace %{param} placeholders
      Object.keys(options).forEach(paramKey => {
        const placeholder = `%{${paramKey}}`
        const value = options[paramKey]
        text = text.replace(placeholder, value)
      })
      return text
    }

    const message = getPolyglot().t(key, options)

    // polyglot returns the key itself if not found
    if (isEmpty(message) || message === key) {
      if (useKeyIfNotFound) {
        console.error(`[i18n][${key}] Missing translation for key for locale ${locale}`)
        return key
      } else {
        // maybe we want to return nothing (instead of the key)
        // eg. placeholder for optional text
        // Note: will be checked with isNil() in the caller to know if we want to display the label container or not. So we return null (and not an empty string)
        return null
      }
    }

    return message
  } catch (e) {
    console.error(e)
    // console.warn(`I18n: ${key} error when formatting for locale ${locale}`, options)
  }

  return locale
}

export const tr = (key, options = {}) => {
  return trWithLocale(_locale, key, options, true)
}

/**
 * returns an empty string (and not the key) if the value is empty
 *
 * useful for optional texts
 */
export const trOptional = (key, options = {}) => {
  return trWithLocale(_locale, key, options, false)
}
